// Verify.js

// Import necessary hooks and Firebase functions
import { useEffect, useState } from 'react'; // React hooks for state management
import { auth, db } from './firebase'; // Firebase authentication and Firestore instances
import {
  signInWithEmailLink,
  isSignInWithEmailLink,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth'; // Firebase auth functions
import { doc, updateDoc, getDoc } from 'firebase/firestore'; // Firestore functions
import './App.css'; // Ensure styles are included

// Verify component to handle email verification
function Verify() {
  // State variables for verification
  const [loading, setLoading] = useState(true); // Loading state
  const [email, setEmail] = useState(''); // Email input for manual entry

  // State variables for modal control
  const [showModal, setShowModal] = useState(false); // Modal visibility
  const [modalMessage, setModalMessage] = useState(''); // Message for the modal

  /**
   * Helper function to extract the 'email' parameter from the URL.
   */
  const getEmailFromUrl = () => {
    const params = new URLSearchParams(window.location.search); // Parse query parameters
    const emailParam = decodeURIComponent(params.get('email')); // Decode the email parameter
    return emailParam;
  };

  /**
   * useEffect hook to run the verification logic on component mount.
   */
  useEffect(() => {
    const verifyEmail = async () => {
      const emailFromUrl = getEmailFromUrl();
      setEmail(emailFromUrl);

      if (!emailFromUrl) {
        setModalMessage('Email is missing. Please try signing up again.');
        setShowModal(true);
        setLoading(false);
        return;
      }

      try {
        await setPersistence(auth, browserSessionPersistence);

        const docRef = doc(db, 'signup', emailFromUrl);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().verified) {
          setModalMessage('Your email is already verified.');
          setShowModal(true);
          setLoading(false);
          return;
        }

        if (isSignInWithEmailLink(auth, window.location.href)) {
          await signInWithEmailLink(auth, emailFromUrl, window.location.href);
          await updateDoc(docRef, { verified: true });

          setModalMessage('Email verification successful! You will be notified when the game begins.');
        } else {
          setModalMessage('Invalid or expired verification link. Please try signing up again.');
        }
      } catch (error) {
        console.error('Verification error:', error); // Improved error logging
        setModalMessage('An error occurred during verification. Please try again.');
      } finally {
        setShowModal(true);
        setLoading(false);
      }
    };

    verifyEmail();
  }, []); // Empty dependency array ensures this runs only once

  /**
   * Fallback to manually enter the email if session persistence is lost.
   */
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        await signInWithEmailLink(auth, email, window.location.href);

        const docRef = doc(db, 'signup', email);
        await updateDoc(docRef, { verified: true });

        setModalMessage('Email verification successful!');
      } else {
        setModalMessage('Invalid or expired link.');
      }
    } catch (error) {
      console.error('Manual email submission error:', error); // Improved error logging
      setModalMessage('Verification failed. Please try again.');
    } finally {
      setShowModal(true);
      setLoading(false);
    }
  };

  // Render the component UI
  return (
    <div>
      {showModal && (
        <div className="blur-overlay" aria-live="assertive">
          <div className="modal-message">
            {loading ? (
              <p>Verifying...</p>
            ) : (
              <>
                <p>{modalMessage}</p>
                <button className="dismiss-button" onClick={() => setShowModal(false)}>Okay</button>
              </>
            )}
          </div>
        </div>
      )}
      {!email && !loading && (
        <form onSubmit={handleEmailSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />
          <button type="submit">Verify Email</button>
        </form>
      )}
    </div>
  );
}

export default Verify;
